import { useState, useRef, useEffect, useCallback, type JSX } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Bell,
  BellOff,
  Sun,
  Moon,
  AlignJustify,
  AlignLeft,
  Undo2,
  Redo2,
  RefreshCw,
  X,
  ShoppingCart,
  XCircle,
  AlertCircle,
  AlertTriangle,
  FileText,
  Upload,
  CheckSquare,
  CreditCard,
  HelpCircle,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';
import { useNotifications, type NotificationItem } from '../../hooks/useNotifications';
import { useHelpOverlay } from '../../hooks/useHelpOverlay';
import { resolveDocSlug } from '../../lib/docsRegistry';
import TopBarDraftsDropdown from './TopBarDraftsDropdown';
import WhatsNewWidget from './WhatsNewWidget';
import GlobalSearch from './GlobalSearch';
import { useTickerMessage } from '../../lib/activityTicker';

// ── Event icon map ────────────────────────────────────────────────────────────

const EVENT_ICONS: Record<string, LucideIcon> = {
  po_submitted: ShoppingCart,
  po_approved: ShoppingCart,
  po_ordered: ShoppingCart,
  po_received: ShoppingCart,
  po_rejected: XCircle,
  po_cancelled: XCircle,
  po_chain_cancelled: XCircle,
  po_edit_required: AlertCircle,
  po_discrepancy_flagged: AlertTriangle,
  receiving_discrepancy: AlertTriangle,
  po_invoice_assigned: FileText,
  po_processing_complete: FileText,
  file_uploaded: Upload,
  task_assigned: CheckSquare,
  task_overdue: CheckSquare,
  low_credit_warning: CreditCard,
  system_alert: Bell,
};

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Balance badge ─────────────────────────────────────────────────────────────

interface CreditBadgeProps {
  label: string;
  value: number | null | undefined;
  color?: string;
}

function CreditBadge({
  label,
  value,
  color = 'text-ink-secondary',
}: CreditBadgeProps): JSX.Element {
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-2xs text-ink-muted uppercase tracking-wider">{label}</span>
      <span className={clsx('text-sm font-semibold font-mono', color)}>
        {value !== null && value !== undefined
          ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          : '\u2014'}
      </span>
    </div>
  );
}

// ── Notification panel ────────────────────────────────────────────────────────

interface NotificationPanelProps {
  notifications: NotificationItem[];
  onMarkRead: (id: string) => void | Promise<void>;
  onMarkAllRead: () => void | Promise<void>;
  onClose: () => void;
  onNavigate: (link: string) => void;
}

function NotificationPanel({
  notifications,
  onMarkRead,
  onMarkAllRead,
  onClose,
  onNavigate,
}: NotificationPanelProps): JSX.Element {
  return (
    <div
      className="fixed top-[52px] right-0 w-80 h-[calc(100vh-52px-36px)] bg-surface-1 border-l border-border shadow-lg flex flex-col"
      style={{ zIndex: 'var(--z-dropdown, 100)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        <span className="text-sm font-semibold text-ink-bright">Notifications</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onMarkAllRead()}
            className="text-2xs text-accent hover:text-accent-hover transition-colors cursor-pointer"
          >
            Mark all read
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
            aria-label="Close notifications"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto pr-2.5">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <BellOff size={48} className="text-ink-muted mb-3" />
            <p className="text-sm text-ink-muted">No notifications</p>
          </div>
        ) : (
          notifications.map((n) => {
            const Icon = (n.event && EVENT_ICONS[n.event]) || Bell;
            return (
              <button
                key={n.id}
                onClick={() => {
                  if (!n.read) onMarkRead(n.id);
                  if (n.link) onNavigate(n.link);
                }}
                className={clsx(
                  'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors cursor-pointer border-b border-border-subtle',
                  n.read ? 'bg-surface-0 hover:bg-surface-2' : 'bg-surface-2 hover:bg-surface-3'
                )}
              >
                {/* Unread dot */}
                <div className="w-2 flex-shrink-0 pt-1.5">
                  {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-accent" />}
                </div>

                {/* Icon */}
                <Icon size={16} className="text-ink-muted flex-shrink-0 mt-0.5" />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p
                    className={clsx(
                      'text-xs font-medium truncate',
                      n.read ? 'text-ink-muted' : 'text-ink-primary'
                    )}
                  >
                    {n.title}
                  </p>
                  <p
                    className={clsx(
                      'text-xs mt-0.5 line-clamp-2',
                      n.read ? 'text-ink-muted' : 'text-ink-secondary'
                    )}
                  >
                    {n.message}
                  </p>
                </div>

                {/* Timestamp */}
                <span className="text-2xs text-ink-muted font-mono flex-shrink-0 mt-0.5">
                  {relativeTime(n.created_at)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── TopBar ────────────────────────────────────────────────────────────────────

export default function TopBar(): JSX.Element {
  const {
    theme,
    toggleTheme,
    density,
    toggleDensity,
    sidebarCollapsed,
    setSidebarCollapsed,
    creditBalance,
    cashBalance,
    isDistRep,
    user,
  } = useApp();

  const navigate = useNavigate();
  const location = useLocation();
  const { open: openHelp } = useHelpOverlay();
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bellRef = useRef<HTMLButtonElement | null>(null);

  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(user?.id);
  const tickerMessage = useTickerMessage();

  // Pulse animation on bell when unread count increases
  const [bellPulse, setBellPulse] = useState<boolean>(false);
  const prevUnreadRef = useRef<number>(unreadCount);
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setBellPulse(true);
      const t = setTimeout(() => setBellPulse(false), 4600);
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Close panel on click outside
  useEffect(() => {
    if (!panelOpen) return;
    function handleClick(e: MouseEvent): void {
      const target = e.target as Node | null;
      if (panelRef.current?.contains(target)) return;
      if (bellRef.current?.contains(target)) return;
      setPanelOpen(false);
    }
    function handleEscape(e: KeyboardEvent): void {
      if (e.key === 'Escape') setPanelOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [panelOpen]);

  // Day 62b — notification links can now be absolute URLs (cross-subdomain
  // to arbi-dev.ironcladsupply.co/admin/feedback?id=...) per the
  // send-feedback Edge Function update. React Router's navigate() can
  // only handle same-origin paths; absolute URLs must use
  // window.location.assign for the browser to handle the cross-origin
  // navigation. Future-proofs Day 64 Build Tracker → arbi-root cross-
  // subdomain notifications too.
  const handleNavigate = useCallback(
    (link: string): void => {
      setPanelOpen(false);
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.location.assign(link);
      } else {
        navigate(link);
      }
    },
    [navigate]
  );

  return (
    <header className="flex items-center gap-2 px-3 h-[52px] bg-topbar flex-shrink-0">
      {/* Left zone: Logo + sidebar toggle */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Logo — button (not div with role=link) so keyboard users can activate it */}
        <button
          type="button"
          className="flex items-center cursor-pointer select-none bg-transparent border-0 p-0"
          onClick={() => navigate('/dashboard')}
          aria-label="Go to Dashboard"
        >
          <img src="/arbi-wordmark-dark.svg" alt="Arbi" className="h-12 flex-shrink-0" />
        </button>

        {/* Sidebar toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>
      </div>

      {/* Center zone: ISG bolt + activity ticker */}
      <div className="flex-1 flex items-center justify-center gap-2 min-w-0 overflow-hidden">
        {/* ISG bolt — hardcoded for V1 single-tenant. Multi-tenant moves this
            to organization.logo_url per Phase 4 BACKLOG entry. */}
        <img
          src="/isg-bolt.svg"
          alt=""
          aria-hidden="true"
          className="h-4 flex-shrink-0 opacity-70"
        />
        <span
          className={clsx(
            'text-xs text-ink-secondary truncate transition-opacity duration-200',
            tickerMessage ? 'opacity-100' : 'opacity-0'
          )}
          aria-live="polite"
        >
          {tickerMessage || '\u00A0'}
        </span>
      </div>

      {/* Search */}
      <GlobalSearch />

      {/* Credit & Cash — click navigates to Finance → Credit & Cash. F12 (Day 99a):
          hidden for dist_rep (Architecture Rule 4 — zero financial data). */}
      {!isDistRep && (
        <button
          onClick={() => navigate('/finance/credit-cash')}
          className="hidden lg:flex items-center gap-4 px-3 border-l border-white/10 cursor-pointer hover:bg-white/5 rounded transition-colors"
        >
          <CreditBadge
            label="Credit"
            value={creditBalance}
            color={
              creditBalance === null
                ? 'text-ink-secondary'
                : creditBalance > 0
                  ? 'text-success'
                  : 'text-danger'
            }
          />
          <CreditBadge label="Cash" value={cashBalance} color="text-warning" />
        </button>
      )}

      {/* Undo / Redo / Refresh */}
      <div className="flex items-center gap-0.5 border-l border-white/10 pl-2">
        <button
          className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
          title="Undo"
          aria-label="Undo"
        >
          <Undo2 size={14} />
        </button>
        <button
          className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
          title="Redo"
          aria-label="Redo"
        >
          <Redo2 size={14} />
        </button>
        <button
          className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
          title="Refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Density + theme toggles */}
      <div className="flex items-center gap-0.5 border-l border-white/10 pl-2">
        <button
          onClick={toggleDensity}
          className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
          title={`Density: ${density}`}
          aria-label="Toggle density"
        >
          {density === 'comfortable' ? <AlignJustify size={14} /> : <AlignLeft size={14} />}
        </button>
        <button
          onClick={toggleTheme}
          className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </button>
      </div>

      {/* Day 76 P5.1 Phase 8E — buying session drafts dropdown */}
      <TopBarDraftsDropdown />

      {/* Day 100 — "What's new" release-notes widget (plain bullets; no MarkdownView) */}
      <WhatsNewWidget />

      {/* Day 100b.2 — contextual help: a single route-aware "?" (no shared
          page-header exists) → overlay Docs tab at the current surface's slug. */}
      <button
        onClick={() => openHelp({ tab: 'docs', slug: resolveDocSlug(location.pathname) })}
        className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
        title="Help for this page"
        aria-label="Help for this page"
      >
        <HelpCircle size={14} />
      </button>

      {/* Notification bell */}
      <button
        ref={bellRef}
        onClick={() => setPanelOpen((prev) => !prev)}
        className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer relative"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={14} />
        {unreadCount > 0 && (
          <span
            className={clsx(
              'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-danger text-white text-2xs font-semibold flex items-center justify-center px-1 leading-none',
              bellPulse && 'pulse'
            )}
            onAnimationEnd={() => setBellPulse(false)}
            aria-live="polite"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification panel */}
      {panelOpen && (
        <div ref={panelRef}>
          <NotificationPanel
            notifications={notifications}
            onMarkRead={markAsRead}
            onMarkAllRead={markAllAsRead}
            onClose={() => setPanelOpen(false)}
            onNavigate={handleNavigate}
          />
        </div>
      )}
    </header>
  );
}
